/**
 * 回铃音(V2 L1)—— 把「正在接通…」的 4~8s 空等变成真的在打电话。
 *
 * 用 WebAudio 实时合成,**不塞任何音频文件**:一段 mp3 要么走网络(接通更慢,还可能 404)、
 * 要么塞成 base64 把 h5 包撑大,而回铃音本来就是一个正弦波。
 *
 * 节奏按国标:450Hz,响 1 秒停 4 秒。别改成连续响 —— 连续的正弦波听起来像故障音,
 * 不像电话。
 *
 * 淡入淡出各 30ms:直接开关振荡器会有「啪」的爆音(方波沿),在耳机里很难受。
 */
import { useEffect } from "react";

const RINGBACK_HZ = 450; // 国标回铃音频率
const RING_MS = 1000; // 响
const CYCLE_MS = 5000; // 一个周期(响 1 秒 + 停 4 秒)
const FADE_S = 0.03; // 淡入淡出,消爆音
const PEAK_GAIN = 0.12; // 小声 —— 这是背景等待音,不是闹铃

type Ctor = typeof AudioContext;

function getAudioCtor(): Ctor | null {
  const w = window as unknown as { AudioContext?: Ctor; webkitAudioContext?: Ctor };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

/**
 * @param active 是否处于拨号中
 * @param muted  用户是否关掉了声音(门店/开放办公区外放尴尬)
 */
export function useRingback(active: boolean, muted: boolean): void {
  useEffect(() => {
    if (!active || muted) return;
    const Ctor = getAudioCtor();
    if (!Ctor) return; // 老 webview 没 WebAudio:静默降级成无声拨号,不炸

    let ctx: AudioContext;
    try {
      ctx = new Ctor();
    } catch {
      return;
    }
    void ctx.resume?.().catch(() => undefined);

    let stopped = false;
    const beep = () => {
      if (stopped) return;
      try {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = RINGBACK_HZ;
        const t0 = ctx.currentTime;
        const t1 = t0 + RING_MS / 1000;
        gain.gain.setValueAtTime(0, t0);
        gain.gain.linearRampToValueAtTime(PEAK_GAIN, t0 + FADE_S);
        gain.gain.setValueAtTime(PEAK_GAIN, t1 - FADE_S);
        gain.gain.linearRampToValueAtTime(0, t1);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start();
        osc.stop(t1);
      } catch {
        // 单次响铃失败不值得中断整个拨号
      }
    };

    beep(); // 立刻响第一声,不等一个周期(点了按钮要马上有反馈)
    const timer = setInterval(beep, CYCLE_MS);

    return () => {
      stopped = true;
      clearInterval(timer);
      void ctx.close?.().catch(() => undefined);
    };
  }, [active, muted]);
}
